// Übersetzt WMS-GetFeatureInfo-Antworten der ausländischen Drohnen-Geozonen
// (CH/BAZL, NL/LVNL, LU/Geoportail, FR/DGAC) in deutschsprachige Popups für
// die Web-Planungskarte. Pro Land kommt das Upstream-Format in einer anderen
// Sprache und Struktur an — diese Datei kapselt das Parsen + Übersetzen, damit
// `MapPage` nur noch HTML-Zeilen einsetzen muss.
//
// Referenz für die Übersetzungstabellen: `client/lib/geozones.ts` (Mobile).

export type Country = "DE" | "CH" | "LU" | "NL" | "FR";

// CH (geo.admin.ch) unterstützt KEIN INFO_FORMAT=text/html (ServiceException),
// liefert aber bei text/plain die mehrsprachigen Felder inkl. `_de`-Varianten.
export function infoFormatForCountry(country: Country): string {
  return country === "CH" ? "text/plain" : "text/html";
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export function renderFeatureInfo(raw: string, country: Country): string {
  const html = (raw || "").trim();
  if (!html) return "";
  switch (country) {
    case "CH":
      return renderChPlain(html);
    case "NL":
      return renderNlTable(html);
    case "LU":
      return renderLuLabels(html);
    case "FR":
      return renderHtmlTable(html, true);
    default:
      return renderHtmlTable(html, false);
  }
}

function row(label: string, value: string): string {
  return `<div><b>${escapeHtml(label)}:</b> ${escapeHtml(value)}</div>`;
}

// ---------------------------------------------------------------------------
// FR / DE — generische WMS-GetFeatureInfo-HTML-Tabelle (wie zuvor sanitizeWmsHtml)
// ---------------------------------------------------------------------------

function renderHtmlTable(html: string, isFr: boolean): string {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return "";
  }
  const normKey = (key: string) => key.trim().toLowerCase().replace(/[:\s]+$/, "");
  const isLimiteKey = (key: string) => normKey(key) === "limite";
  const isRemarqueKey = (key: string) => normKey(key) === "remarque";
  const trFr = (key: string, value: string): string => {
    if (isLimiteKey(key)) return translateFrLimite(value);
    if (isRemarqueKey(key)) return translateFrRemarque(value);
    return value;
  };
  const rows: string[] = [];
  for (const table of Array.from(doc.querySelectorAll("table"))) {
    const headers = Array.from(table.querySelectorAll("th")).map((th) => (th.textContent || "").trim());
    for (const tr of Array.from(table.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("td")).map((td) => (td.textContent || "").trim());
      if (cells.length === 0) continue;
      if (cells.length === 2) {
        const value = isFr ? trFr(cells[0], cells[1]) : cells[1];
        if (value) rows.push(`<div><b>${escapeHtml(cells[0])}:</b> ${escapeHtml(value)}</div>`);
      } else if (headers.length === cells.length) {
        const parts = cells
          .map((c, i) => {
            if (!c) return "";
            const value = isFr ? trFr(headers[i] || "", c) : c;
            if (!value) return "";
            return `<b>${escapeHtml(headers[i] || "")}:</b> ${escapeHtml(value)}`;
          })
          .filter(Boolean);
        if (parts.length) rows.push(`<div>${parts.join(" · ")}</div>`);
      } else {
        rows.push(`<div>${cells.map((c) => escapeHtml(c)).join(" · ")}</div>`);
      }
    }
  }
  if (rows.length === 0) {
    let text = (doc.body?.textContent || "").trim();
    if (text.length < 5 || text.length > 800) return "";
    if (isFr) text = text.replace(/interdit\w*/gi, "Flugverbot");
    return `<div>${escapeHtml(text)}</div>`;
  }
  return rows.slice(0, 30).join("");
}

// Akzent-/Case-unabhängige Normalisierung für den Abgleich französischer Texte.
function normalizeFr(s: string): string {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Phrasen-Ersetzungen für unbekannte DGAC-`remarque`-Freitexte (Reihenfolge:
// spezifischer zuerst), damit auch nicht standardisierte Sätze ins Deutsche
// übertragen werden, statt französisch stehenzubleiben.
const FR_REMARQUE_PHRASES: { re: RegExp; de: string }[] = [
  { re: /en\s+espace\s+public/gi, de: "im öffentlichen Raum" },
  { re: /sauf\s+conform[ée]ment\s+[àa]\s+l['’]arr[êe]t[ée]\s+Espace/gi, de: "außer gemäß dem Erlass „Espace“" },
  { re: /altitude\s+de\s+r[ée]f[ée]rence\s+de\s+la\s+plateforme\s*:?\s*(\d+)\s*m/gi, de: "Referenzhöhe der Plattform: $1 m" },
  { re: /hauteur\s+maximale\s+de\s+vol\s+de\s+(\d+)\s*m(?:[èe]tres)?/gi, de: "maximale Flughöhe $1 m" },
  { re: /au-?dessus\s+de\s+l['’]espace\s+public/gi, de: "über öffentlichem Raum" },
  { re: /sauf\s+autorisation\s+pr[ée]fectorale/gi, de: "außer mit präfektoraler Genehmigung" },
  { re: /sauf\s+autorisation/gi, de: "außer mit Genehmigung" },
  { re: /pour\s+la\s+cat[ée]gorie\s+ouverte/gi, de: "für die offene Kategorie" },
  { re: /ou\s+l['’]a[ée]romod[ée]lisme/gi, de: "oder Modellflug" },
  { re: /l['’]a[ée]romod[ée]lisme/gi, de: "Modellflug" },
  { re: /a[ée]romod[ée]lisme/gi, de: "Modellflug" },
  { re: /interdiction\s+de\s+survol/gi, de: "Überflugverbot" },
  { re: /[ée]volution\s+interdite/gi, de: "Betrieb verboten" },
  { re: /vol\s+interdit/gi, de: "Flugverbot" },
  { re: /en\s+agglom[ée]ration/gi, de: "in geschlossenen Ortschaften" },
];

// Übersetzt das DGAC-Freitextfeld `remarque` ins Deutsche. Standardisierte
// Komplettsätze werden sauber formuliert, der Rest satzweise per Phrasen-
// Ersetzung übertragen (Fallback: Originaltext, falls nichts greift).
export function translateFrRemarque(remarque: string): string {
  const v = (remarque || "").replace(/\*+\s*$/, "").trim();
  if (!v || v.toLowerCase() === "null") return "";
  const norm = normalizeFr(v);
  const priorNotification = norm.match(
    /notification prealable obligatoire pour les aeronefs de masse superieure a (\d+)\s*g/,
  );
  if (
    priorNotification &&
    norm.includes("sans prejudice") &&
    norm.includes("restrictions de survol")
  ) {
    return (
      `Eine vorherige Meldung ist für Luftfahrzeuge mit einer Masse über ${priorNotification[1]} g verpflichtend; ` +
      "die sonstigen geltenden Überflugbeschränkungen sind dennoch einzuhalten."
    );
  }
  const platformAltitude = norm.match(
    /altitude de reference de la plateforme\s*:?\s*(\d+)\s*m/,
  );
  if (platformAltitude) return `Referenzhöhe der Plattform: ${platformAltitude[1]} m.`;
  if (
    norm.includes("evolution interdite") &&
    norm.includes("espace public") &&
    norm.includes("agglomeration") &&
    norm.includes("arrete espace")
  ) {
    return "Im öffentlichen Raum in geschlossenen Ortschaften ist der Betrieb verboten, außer gemäß dem Erlass „Espace“.";
  }
  const onlyHeight = norm.match(/^hauteur maximale de vol de (\d+)\s*m(?:etres)?\.?$/);
  if (onlyHeight) return `Maximale Flughöhe ${onlyHeight[1]} m.`;
  if (
    norm.includes("evolution interdite") &&
    norm.includes("agglomeration") &&
    norm.includes("prefectoral")
  ) {
    return (
      "Betrieb über öffentlichem Raum in geschlossenen Ortschaften verboten – " +
      "außer mit präfektoraler Genehmigung (offene Kategorie oder Modellflug)."
    );
  }
  if (/^vol interdit\.?$/.test(norm)) return "Flugverbot.";
  let out = v;
  for (const { re, de } of FR_REMARQUE_PHRASES) out = out.replace(re, de);
  return out;
}

// Übersetzt den DGAC/Géoplateforme-Wert `limite` (Höhengrenze bzw. "Vol interdit").
// Akzeptiert nackte Zahlen ("50") und ganze Sätze ("Hauteur maximale de vol de 50 m").
export function translateFrLimite(limite: string): string {
  const v = (limite || "").replace(/\*+\s*$/, "").trim();
  if (!v || v.toLowerCase() === "null") return "";
  const lower = normalizeFr(v);
  if (lower.includes("interdit")) return "Flugverbot";
  const m = lower.match(/(\d+)\s*m\b/) || lower.match(/^(\d+)(?:[.,]\d+)?$/);
  if (m) return `Maximale Flughöhe ${m[1]} m`;
  return translateFrRemarque(v);
}

// ---------------------------------------------------------------------------
// NL / LVNL — HTML-Tabelle mit niederländischen Spalten + Werten
// ---------------------------------------------------------------------------

const NL_HEADER_DE: Record<string, string> = {
  localtype: "Typ",
  sourceTxt: "Beschreibung",
  source: "Beschreibung",
  name: "Name",
  naam: "Name",
};

// Spalten ohne Mehrwert für den Piloten (interne IDs / Geometrie) werden verworfen.
function isNoiseNlKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return k === "fuuid" || k === "objectid" || k.endsWith("id") || k.endsWith("uuid") || k.includes("shape");
}

function renderNlTable(html: string): string {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return "";
  }
  const rows: string[] = [];
  for (const table of Array.from(doc.querySelectorAll("table"))) {
    const headers = Array.from(table.querySelectorAll("th")).map((th) => (th.textContent || "").trim());
    if (headers.length === 0) continue;
    for (const tr of Array.from(table.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("td")).map((td) => (td.textContent || "").trim());
      if (cells.length === 0) continue;
      const parts: string[] = [];
      cells.forEach((c, i) => {
        const key = headers[i] || "";
        if (!c || isNoiseNlKey(key)) return;
        const label = NL_HEADER_DE[key] || key;
        const value = translateNlValue(key, c);
        parts.push(`<b>${escapeHtml(label)}:</b> ${escapeHtml(value)}`);
      });
      if (parts.length) rows.push(`<div>${parts.join("<br/>")}</div>`);
    }
  }
  return rows.slice(0, 30).join("");
}

const NL_LOCALTYPE_DE: Record<string, string> = {
  Verboden: "Verboten",
  Beperking: "Beschränkt",
  Beperkt: "Beschränkt",
  CTR: "Kontrollzone (CTR)",
  TMA: "Nahverkehrsbereich (TMA)",
  Natura2000: "Natura-2000-Schutzgebiet",
  Militair: "Militärgebiet",
  Laagvlieggebied: "Tieffluggebiet",
  Gevaarlijk: "Gefahrengebiet",
};

const NL_SOURCE_DE: Record<string, string> = {
  "Gecontroleerde luchthavens en/of radioverplichting (Verboden voor open categorie)": "Kontrollierte Flughäfen und/oder Funkpflicht (Verboten für offene Kategorie)",
  "Gecontroleerde luchthavens (Verboden voor open categorie)": "Kontrollierte Flughäfen (Verboten für offene Kategorie)",
  "Radioverplichting (Verboden voor open categorie)": "Funkpflicht (Verboten für offene Kategorie)",
  "Verboden voor open categorie": "Verboten für offene Kategorie",
  "Beperkt voor open categorie": "Beschränkt für offene Kategorie",
};

const NL_WORD_DE: [RegExp, string][] = [
  [/\bGecontroleerde luchthavens\b/g, "Kontrollierte Flughäfen"],
  [/\bradioverplichting\b/gi, "Funkpflicht"],
  [/\bVerboden voor open categorie\b/gi, "Verboten für offene Kategorie"],
  [/\bBeperkt voor open categorie\b/gi, "Beschränkt für offene Kategorie"],
  [/\bToegestaan met beperkingen\b/gi, "Erlaubt mit Einschränkungen"],
  [/\bToegestaan\b/gi, "Erlaubt"],
  [/\bBeperkingen\b/gi, "Einschränkungen"],
  [/\bBeperking\b/gi, "Einschränkung"],
  [/\bBeperkt\b/gi, "Beschränkt"],
  [/\bVerboden\b/g, "Verboten"],
  [/\blaagvliegrouten\b/gi, "Tiefflugrouten"],
  [/\blaagvliegroute\b/gi, "Tiefflugstrecke"],
  [/\blaagvlieggebieden\b/gi, "Tieffluggebiete"],
  [/\blaagvlieggebied\b/gi, "Tieffluggebiet"],
  [/\bindustriegebieden\b/gi, "Industriegebiete"],
  [/\bindustriegebied\b/gi, "Industriegebiet"],
  [/\bhavens\b/gi, "Häfen"],
  [/\bhaven\b/gi, "Hafen"],
  [/\bmilitair\b/gi, "Militärisch"],
  [/\boefengebied\b/gi, "Übungsgebiet"],
  [/\bschietrange\b/gi, "Schießanlage"],
  [/\bluchthavens\b/gi, "Flughäfen"],
  [/\bluchthaven\b/gi, "Flughafen"],
  [/\bvliegveld\b/gi, "Flugplatz"],
  [/\bvliegbasis\b/gi, "Luftwaffenstützpunkt"],
  [/\bnatura\s*2000\b/gi, "Natura-2000"],
  [/\bnatuurgebied(en)?\b/gi, "Naturgebiet$1"],
  [/\bnatuur\b/gi, "Natur"],
  [/\ben\/of\b/g, "und/oder"],
  [/\ben\b/g, "und"],
  [/\bvoor\b/g, "für"],
  [/\bmet\b/g, "mit"],
  [/\bopen categorie\b/gi, "offene Kategorie"],
];

function translateNlSource(source: string): string {
  if (!source) return source;
  const exact = NL_SOURCE_DE[source];
  if (exact) return exact;
  let result = source;
  for (const [pattern, replacement] of NL_WORD_DE) result = result.replace(pattern, replacement);
  return result;
}

export function translateNlValue(key: string, value: string): string {
  const k = key.trim().toLowerCase();
  if (k === "localtype") return NL_LOCALTYPE_DE[value] || value;
  if (k === "sourcetxt" || k === "source") return translateNlSource(value);
  return value;
}

// ---------------------------------------------------------------------------
// LU / Geoportail — HTML mit <label>/<span>-Paaren, Schlüssel + Werte FR/EN
// ---------------------------------------------------------------------------

const LU_KEY_DE: Record<string, string> = {
  "Zone Identifier": "Zone",
  Description: "Beschreibung",
  "Nature of proctection": "Grund",
  Activity: "Status",
  Responsability: "Zuständigkeit",
  Remarks: "Hinweis",
  Conditions: "Bedingungen",
  Exceptions: "Ausnahmen",
};

const LU_REASON_DE: Record<string, string> = {
  AERO: "Flugplatz",
  NATURE: "Naturschutz",
  MILITARY: "Militär",
  SENSITIVE: "Sensibel",
  PRIVACY: "Privatsphäre",
  TERR: "Gebietsschutz",
  NUCLEAR: "Kernanlage",
  PRISON: "Justizvollzug",
  HOSPITAL: "Krankenhaus",
  INDUSTRIAL: "Industrieanlage",
  OTHER: "Sonstige",
};

const LU_ACTIVITE_DE: Record<string, string> = {
  active: "Aktiv",
  inactive: "Inaktiv",
  permanent: "Dauerhaft",
  temporary: "Vorübergehend",
  actif: "Aktiv",
  inactif: "Inaktiv",
  temporaire: "Vorübergehend",
};

function isNoiseLuKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return (
    k.includes("st_perimeter") ||
    k.includes("st_area") ||
    k.includes("shape") ||
    k === "has_sketch" ||
    k === "objectid" ||
    k === "zone state" ||
    k === "restriction type" ||
    k.startsWith("created_") ||
    k.startsWith("last_edited_")
  );
}

function luDate(value: string): string {
  const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  if (!m) return value;
  return `${m[1]}.${m[2]}.${m[3]}, ${m[4]}:${m[5]}`;
}

function renderLuLabels(html: string): string {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return "";
  }
  // Erst alle label/span-Paare einsammeln (Schlüssel ggf. mit `f_`-Präfix).
  const props: Record<string, string> = {};
  const labels = Array.from(doc.querySelectorAll("label"));
  for (const label of labels) {
    let key = (label.textContent || "").trim().replace(/^f_/, "");
    let value = "";
    const span = label.nextElementSibling;
    if (span && span.tagName.toLowerCase() === "span") value = (span.textContent || "").trim();
    if (key && value) props[key] = value;
  }
  // Fallback: kein label/span-Layout → generische Tabelle versuchen.
  if (Object.keys(props).length === 0) return renderHtmlTable(html, false);

  const rows: string[] = [];

  const zone = props["Zone Identifier"];
  if (zone) rows.push(`<div><b>${escapeHtml(zone)}</b></div>`);

  const desc = props["Description"];
  if (desc) rows.push(row("Beschreibung", translateLuFrench(desc)));

  const nature = props["Nature of proctection"];
  if (nature) rows.push(row("Grund", LU_REASON_DE[nature] || translateLuFrench(nature)));

  const activity = props["Activity"];
  if (activity) rows.push(row("Status", LU_ACTIVITE_DE[activity.toLowerCase()] || translateLuFrench(activity)));

  const minH = props["Lower limit (meter)"];
  const maxH = props["Upper limit (meter)"];
  if (minH || maxH) {
    const lo = minH ? `${parseFloat(minH)}` : "0";
    const hi = maxH ? `${parseFloat(maxH)}` : "?";
    rows.push(row("Höhe", `${lo} – ${hi} m`));
  }

  const start = props["Start date and time"];
  const end = props["End date and time"];
  if (start) rows.push(row("Von", luDate(start)));
  if (end) rows.push(row("Bis", luDate(end)));

  const resp = props["Responsability"];
  if (resp) rows.push(row("Zuständigkeit", translateLuFrench(resp)));

  const dur = props["Duration of previsualisation (hours)"];
  if (dur && parseFloat(dur) > 0) rows.push(row("Vorankündigung", `${parseFloat(dur)} Std.`));

  const conditions = props["Conditions"];
  if (conditions) rows.push(row("Bedingungen", translateLuFrench(conditions)));

  const exceptions = props["Exceptions"];
  if (exceptions) rows.push(row("Ausnahmen", translateLuFrench(exceptions)));

  const remarks = props["Remarks"];
  if (remarks) rows.push(row("Hinweis", translateLuFrench(remarks)));

  // Übrige (nicht ausdrücklich behandelte, nicht-technische) Felder anhängen.
  const handled = new Set([
    "Zone Identifier", "Description", "Nature of proctection", "Activity",
    "Lower limit (meter)", "Upper limit (meter)", "Start date and time",
    "End date and time", "Responsability", "Duration of previsualisation (hours)",
    "Conditions", "Exceptions", "Remarks",
  ]);
  for (const [key, value] of Object.entries(props)) {
    if (handled.has(key) || isNoiseLuKey(key) || !value) continue;
    rows.push(row(LU_KEY_DE[key] || key, translateLuFrench(value)));
  }

  return rows.slice(0, 30).join("");
}

const LU_FR_DE_TRANSLATIONS: Record<string, string> = {
  "Aérodrome public": "Öffentlicher Flugplatz",
  "Aerodrome public": "Öffentlicher Flugplatz",
  "Aérodrome": "Flugplatz",
  "Aeroport": "Flughafen",
  "Héliport": "Hubschrauberlandeplatz",
  "héliport": "Hubschrauberlandeplatz",
  "Hélistation": "Hubschrauberlandeplatz",
  "Zone interdite": "Flugverbotszone",
  "Zone restreinte": "Beschränktes Gebiet",
  "Zone dangereuse": "Gefahrengebiet",
  "Réserve naturelle": "Naturschutzgebiet",
  "Zone de protection": "Schutzzone",
  "Zone de contrôle": "Kontrollzone",
  "Espace aérien contrôlé": "Kontrollierter Luftraum",
  "Zone militaire": "Militärgebiet",
  "Centre pénitentiaire": "Justizvollzugsanstalt",
  "Établissement pénitentiaire": "Justizvollzugsanstalt",
  "Hôpital": "Krankenhaus",
  "Centrale nucléaire": "Kernkraftwerk",
  "Parc naturel": "Naturpark",
  "Palais Grand-Ducal": "Großherzoglicher Palast",
  "Autorisation vol ANA et tour": "Fluggenehmigung ANA und Tower",
  "Notification vol ANA et tour": "Flugmeldung ANA und Tower",
  "Autorisation vol ANA": "Fluggenehmigung ANA",
  "Notification vol ANA": "Flugmeldung ANA",
  "Autorisation vol DAC": "Fluggenehmigung DAC",
  "Notification vol DAC": "Flugmeldung DAC",
  "Autorisation vol": "Fluggenehmigung",
  "Notification vol": "Flugmeldung",
  "Autorisation": "Genehmigung",
  "Direction de l'aviation civile": "Direktion der Zivilluftfahrt",
  "Règlement grand-ducal": "Großherzogliche Verordnung",
  "Zones géographiques UAS": "UAS-Geographische Zonen",
  "Zones géographiques": "Geographische Zonen",
  "Activité ponctuelle": "Einmalige Aktivität",
  "Activation à la demande de la Police": "Aktivierung auf Anfrage der Polizei",
  "Activation at the request of the Police": "Aktivierung auf Anfrage der Polizei",
  "DAC for activation at least 6h in advance": "DAC zur Aktivierung mind. 6 Std. im Voraus",
  "DAC for activation at least": "DAC zur Aktivierung mind.",
  "L'activation de la zone est annoncée": "Die Aktivierung der Zone wird angekündigt",
  "L'activation est annoncée": "Die Aktivierung wird angekündigt",
  "Activation is announced": "Aktivierung wird angekündigt",
  "heures à l'avance": "Stunden im Voraus",
  "minutes à l'avance": "Minuten im Voraus",
  "hours in advance": "Stunden im Voraus",
  "minutes in advance": "Minuten im Voraus",
  "au minimum": "mindestens",
  "at least": "mindestens",
  "et est corrélée sur les TSA correspondantes": "und ist an die entsprechenden TSA gekoppelt",
  "and correlated to the corresponding TSA": "und an die entsprechende TSA gekoppelt",
  "Voir NOTAM": "Siehe NOTAM",
  "See NOTAM": "Siehe NOTAM",
  "One-off activity": "Einmalige Aktivität",
  "Seasonal activity": "Saisonale Aktivität",
  "Activité saisonnière": "Saisonale Aktivität",
  "ANA/ARO for activation": "ANA/ARO zur Aktivierung",
  "in advance": "im Voraus",
  "à l'avance": "im Voraus",
  "DAC - Notification LAR/H": "DAC — Meldung LAR/H",
  "Une coordination des services étatiques": "Eine Abstimmung der staatlichen Dienste",
  "avec la tour de contrôle": "mit dem Kontrollturm",
  "de l'aérodrome": "des Flugplatzes",
  "est nécessaire dans tous les cas": "ist in jedem Fall erforderlich",
  "services étatiques": "staatliche Dienste",
  "tour de contrôle": "Kontrollturm",
  "Police/CGDIS": "Polizei/CGDIS",
  "à la demande": "auf Anfrage",
  "Notification": "Meldung",
  "Militaire": "Militärgebiet",
  "août": "August",
  "janvier": "Januar",
  "février": "Februar",
  "mars": "März",
  "avril": "April",
  "mai": "Mai",
  "juin": "Juni",
  "juillet": "Juli",
  "septembre": "September",
  "octobre": "Oktober",
  "novembre": "November",
  "décembre": "Dezember",
};

const LU_SORTED_TRANSLATIONS = Object.entries(LU_FR_DE_TRANSLATIONS).sort((a, b) => b[0].length - a[0].length);

export function translateLuFrench(text: string): string {
  let result = text || "";
  for (const [fr, de] of LU_SORTED_TRANSLATIONS) {
    if (result.includes(fr)) {
      result = result.replace(new RegExp(fr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), de);
    }
  }
  result = result
    .replace(/\bd'([A-ZÀÂÇÉÈÊËÎÏÔÙÛÜŸÆŒ])/g, "von $1")
    .replace(/\bde la\b/gi, "der")
    .replace(/\bde l'/gi, "des ")
    .replace(/\bdu\b/g, "des")
    .replace(/\bde\b/g, "von");
  return result;
}

// ---------------------------------------------------------------------------
// CH / BAZL — text/plain GetFeatureInfo, mehrsprachig inkl. `_de`-Feldern
// ---------------------------------------------------------------------------

const CH_REASON_DE: Record<string, string> = {
  AIR_TRAFFIC: "Luftverkehr",
  NATURE: "Naturschutz",
  PRIVACY: "Privatsphäre",
  SECURITY: "Sicherheit",
  SAFETY: "Sicherheit",
  ENVIRONMENT: "Umwelt",
  NOISE: "Lärmschutz",
  EMERGENCY: "Notfall",
  DEFENSE: "Verteidigung",
  DEFENCE: "Verteidigung",
  OTHER: "Sonstige",
};

function renderChPlain(text: string): string {
  if (/no features were found/i.test(text)) return "";
  // In Features aufteilen: jede "Feature <id>:"-Zeile beginnt einen Block.
  const lines = text.split(/\r?\n/);
  const features: Record<string, string>[] = [];
  let current: Record<string, string> | null = null;
  for (const line of lines) {
    if (/^\s*Feature\s+\S+:/.test(line)) {
      current = {};
      features.push(current);
      continue;
    }
    const m = line.match(/^\s*(\S+)\s*=\s*'(.*)'\s*$/);
    if (m && current) {
      const fullKey = m[1];
      const value = m[2];
      // fullKey z.B. ch.bazl.einschraenkungen-drohnen.zone_name_de.name
      const parts = fullKey.split(".");
      const field = parts.length >= 2 ? parts[parts.length - 2] : fullKey;
      if (value && value.toLowerCase() !== "null") current[field] = value;
    }
  }

  const rows: string[] = [];
  for (const f of features) {
    const featureRows = renderChFeature(f);
    if (featureRows) rows.push(featureRows);
  }
  return rows.slice(0, 6).join('<div style="height:6px"></div>');
}

function renderChFeature(f: Record<string, string>): string {
  const rows: string[] = [];

  const name = f["zone_name_de"] || f["zone_name"];
  if (name) rows.push(`<div><b>${escapeHtml(name)}</b></div>`);

  const restriction = f["zone_restriction_de"] || f["zone_restriction"];
  if (restriction) rows.push(row("Einschränkung", restriction));

  const message = f["zone_message_de"] || f["zone_message"];
  if (message) rows.push(row("Hinweis", message));

  const reason = f["zone_reason_id"];
  if (reason) rows.push(row("Grund", CH_REASON_DE[reason] || reason.replace(/_/g, " ")));

  const lower = f["air_vol_lower_limit"];
  const upper = f["air_vol_upper_limit"];
  if (lower != null || upper != null) {
    const lo = lower != null ? `${lower} m ${f["air_vol_lower_vref"] || ""}`.trim() : "SFC";
    const hi = upper != null ? `${upper} m ${f["air_vol_upper_vref"] || ""}`.trim() : "unbegrenzt";
    rows.push(row("Höhe", `${lo} – ${hi}`));
  }

  if (f["time_permanent"] === "Yes" || f["time_permanent"] === "true") {
    rows.push(row("Gültigkeit", "dauerhaft"));
  } else if (f["time_start"] || f["time_end"]) {
    rows.push(row("Gültigkeit", `${f["time_start"] || "?"} – ${f["time_end"] || "?"}`));
  }

  const auth = f["auth_name_de"] || f["auth_name"];
  if (auth) rows.push(row("Behörde", auth));
  const email = f["auth_email"] || f["auth_contact"];
  if (email) rows.push(row("E-Mail", email));
  const phone = f["auth_phone"];
  if (phone) rows.push(row("Telefon", phone));
  const url = f["auth_url_de"] || f["auth_url"];
  if (url) rows.push(row("Info", url));

  // Fallback für andere BAZL-Layer (z.B. Ruhezonen, Wohngebiete): bekannte
  // Felder fehlen, daher generisch die lesbaren _de-/Klartextfelder zeigen.
  if (rows.length === 0) {
    for (const [key, value] of Object.entries(f)) {
      if (!value || /_fr$|_it$|_en$/.test(key) || /^(label|name)$/.test(key)) continue;
      const pretty = key.replace(/_de$/, "").replace(/_/g, " ");
      rows.push(row(pretty.charAt(0).toUpperCase() + pretty.slice(1), value));
      if (rows.length >= 8) break;
    }
  }

  return rows.join("");
}
